import { FC, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showErrorNotification } from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useCarPurchasesApi } from "../../http/carPurchases/api";
import {
  CarPurchase,
  getCarPurchaseStates,
  getPurchaseTypes,
  isItemArrivingSoon,
  isItemWithExpiredArrivalDate,
} from "../../http/carPurchases/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { downloadFileFromData, formatCurrency, formatDateNumeric, isWithinLast48h } from "../../types/utils";
import SvgAdd from "../icons/Add";
import SvgCancel from "../icons/Cancel";
import SvgCheckCircle from "../icons/CheckCircle";
import SvgEdit from "../icons/Edit";
import SvgError from "../icons/Error";
import SvgTaskAlt from "../icons/TaskAlt";
import EditManufacturerDialog from "../manufacturers/EditManufacturerDialog";
import Button from "../ui/Button";
import { Check } from "../ui/Check";
import CustomLink from "../ui/CustomLink";
import ExportButton from "../ui/ExportButton";
import Header from "../ui/Header";
import { Action, getDeleteAction, getHistoryAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import Panel from "../ui/Panel";
import { State } from "../ui/State";
import Table from "../ui/Table";
import BooleanColumnFilter from "../ui/columnFilters/BooleanColumnFilter";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CarPurchaseHistoryDialog from "./CarPurchaseHistoryDialog";
import DeleteCarPurchaseDialog from "./DeleteCarPurchaseDialog";

const CAR_PURCHASE_FIELDS = [
  "id",
  "createdAt",
  "subsidiary.id",
  "leasingCompany.id",
  "leasingCompany.name",
  "subsidiary.name",
  "supplier.id",
  "supplier.name",
  "driver.id",
  "driver.fullName",
  "manufacturer.id",
  "manufacturer.name",
  "model",
  "optionals",
  "purchaseDate",
  "plannedArrivalDate",
  "costGross",
  "costNet",
  "state",
  "purchaseType",
  "hideArrivalDateNotification",
  "versionNumber",
  "hasOptionals",
] as const;
type CarPurchaseField = (typeof CAR_PURCHASE_FIELDS)[number];
type CarPurchaseSearch = NestedPick<CarPurchase, CarPurchaseField>;

type CarPurchasesProps = {
  isArrivingSoon?: boolean;
};

const CarPurchases: FC<CarPurchasesProps> = ({ isArrivingSoon }) => {
  const i18n = useI18n();
  const carPurchasesApi = useCarPurchasesApi();
  const userSettings = useUserSettings();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const [itemToDelete, setItemToDelete] = useState<CarPurchaseSearch>();
  const navigate = useNavigate();
  const [itemToShowVersions, setItemToShowVersions] = useState<CarPurchaseSearch>();

  const [showManufacturerDialog, setShowManufacturerDialog] = useState<string>();

  const query: PaginationQueryWithFields<CarPurchaseSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: CAR_PURCHASE_FIELDS,
  };

  const navToEdit = (item: CarPurchaseSearch) => {
    navigate(`/admin/car_purchases/${item.id}`);
  };

  const navToAssignItem = (item: CarPurchaseSearch) => {
    navigate(`/admin/cars/new?car_purchase_id=${item.id}`);
  };

  const purchaseTypes = getPurchaseTypes(i18n.t);
  const states = getCarPurchaseStates(i18n.t);

  const toggleNotification = (item: CarPurchaseSearch) => {
    carPurchasesApi
      .upsertAdminCarPurchaseNotification(item.id, {
        hideArrivalDateNotification: !item.hideArrivalDateNotification,
        versionNumber: item.versionNumber,
      })
      .catch((err) => showErrorNotification(i18n, err));
  };

  const getRowActions = (item: CarPurchaseSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.carPurchases.update) {
      actions.push({
        title: i18n.t.common.edit,
        onClick: () => navToEdit(item),
        icon: SvgEdit,
      });
    }

    if (userSettings.permissions.cars.insert) {
      actions.push({
        title: i18n.t.common.assign,
        isDisabled: item.state === "assigned",
        onClick: () => navToAssignItem(item),
        icon: SvgTaskAlt,
      });
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    actions.push({
      title: i18n.t.common.hideArrivalNotification,
      onClick: () => toggleNotification(item),
      visible:
        (!item.hideArrivalDateNotification && isItemArrivingSoon(item.plannedArrivalDate, item.state)) ||
        (!item.hideArrivalDateNotification && isItemWithExpiredArrivalDate(item.plannedArrivalDate, item.state)),
      icon: SvgError,
    });

    actions.push({
      title: i18n.t.common.showArrivalNotification,
      onClick: () => toggleNotification(item),
      visible:
        (item.hideArrivalDateNotification && isItemArrivingSoon(item.plannedArrivalDate, item.state)) ||
        (item.hideArrivalDateNotification && isItemWithExpiredArrivalDate(item.plannedArrivalDate, item.state)),
      icon: SvgError,
    });

    if (userSettings.permissions.carPurchases.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), !!actions.length));
    }

    return actions;
  };

  const result = carPurchasesApi.useAdminCarPurchases(
    query,
    aborter.current.signal,
    isArrivingSoon,
    !!userSettings.permissions.carPurchases.read,
  );
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: carPurchasesApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const baseUrl = `/admin/car_purchases`;

  const viewItems = [
    {
      label: i18n.t.carOrders.all,
      url: baseUrl,
    },
    {
      label: i18n.t.common.arrivingSoon,
      url: `${baseUrl}/arriving_soon`,
    },
  ];

  const title = isArrivingSoon ? i18n.t.common.arrivingSoon : i18n.t.carOrders.all;

  const items = viewItems.filter((item) => item.label !== title);

  const views = { title, items };

  return (
    <>
      <Header title={i18n.t.carOrders.plural} />
      <Panel>
        <Table<CarPurchaseSearch>
          filters={filters}
          searchOptions={{
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          views={views}
          buttons={{
            main: userSettings.permissions.carPurchases.insert && (
              <Button buttonProps={{ onClick: () => navigate("/admin/car_purchases/new") }} glyph={SvgAdd} />
            ),
            secondary: (
              <ExportButton.Excel
                onClick={async () => {
                  const r = await carPurchasesApi.exportExcel(query, aborter.current.signal);
                  r && downloadFileFromData(r?.data, "Car purchases", r?.contentType);
                }}
              />
            ),
          }}
          head={
            <>
              <Table.Th
                title={i18n.t.subsidiaries.singular}
                fieldName="carPurchase.subsidiary.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.suppliers.singular}
                fieldName="carPurchase.supplier.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.leasingCompanies.singular}
                fieldName="carPurchase.leasingCompany.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.recipient}
                fieldName="carPurchase.driver.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.manufacturers.singular}
                fieldName="carPurchase.manufacturer.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th title={i18n.t.common.model} fieldName="carPurchase.model" order={orderOptions} />
              <Table.Th
                title={i18n.t.common.optionals}
                fieldName="carPurchase.hasOptionals"
                filters={filterOptions}
                filter={BooleanColumnFilter}
              />
              <Table.Th title={i18n.t.common.orderDate} fieldName="carPurchase.date" order={orderOptions} />
              <Table.Th
                title={i18n.t.common.plannedArrivalDate}
                fieldName="carPurchase.plannedArrivalDate"
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.priceWithoutVat}
                align="right"
                fieldName="carPurchase.cost.net"
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.priceWithVat}
                align="right"
                fieldName="carPurchase.cost.gross"
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.state}
                fieldName="carPurchase.state"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
                formatter={(value) => states.find((x) => x.value == value)?.label}
              />
              <Table.Th
                title={i18n.t.common.purchaseType}
                fieldName="carPurchase.purchaseType"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
                formatter={(value) => purchaseTypes.find((x) => x.value == value)?.label}
              />
              <th></th>
            </>
          }
          onRowClick={(item) => navigate("/admin/car_purchases/" + item.id)}
          getRowStyle={(item) =>
            isItemArrivingSoon(item.plannedArrivalDate, item.state) && !item.hideArrivalDateNotification
              ? ({ "--row-bg": "#fffed9", "--row-hover-bg": "#f2f0c0" } as React.CSSProperties)
              : isItemWithExpiredArrivalDate(item.plannedArrivalDate, item.state) && !item.hideArrivalDateNotification
                ? ({ "--row-bg": "var(--red-20)", "--row-hover-bg": "var(--red-40)" } as React.CSSProperties)
                : undefined
          }
          renderRow={(item) => (
            <>
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                {userSettings.permissions.subsidiaries.read ? (
                  <CustomLink to={`/admin/subsidiaries/${item.subsidiary.id}`}>{item.subsidiary.name}</CustomLink>
                ) : (
                  item.subsidiary.name
                )}
              </td>
              <td>
                {userSettings.permissions.suppliers.read ? (
                  <CustomLink to={`/admin/suppliers/${item.supplier?.id}`}>{item.supplier?.name}</CustomLink>
                ) : (
                  item.supplier?.name
                )}
              </td>
              <td>
                <CustomLink to={`/admin/leasing_companies/${item.leasingCompany?.id}`}>
                  {item.leasingCompany?.name}
                </CustomLink>
              </td>
              <td>
                {userSettings.permissions.users.read ? (
                  <CustomLink to={`/admin/users/${item.driver?.id}`}>{item.driver?.fullName}</CustomLink>
                ) : (
                  item.driver?.fullName
                )}
              </td>
              <td>
                {userSettings.permissions.manufacturers.read ? (
                  <CustomLink
                    to=""
                    onClick={(e) => {
                      e.preventDefault();
                      setShowManufacturerDialog(item.manufacturer.id);
                    }}
                  >
                    {item.manufacturer.name}
                  </CustomLink>
                ) : (
                  item.manufacturer.name
                )}
              </td>
              <td>{item.model}</td>
              <td>
                <Check
                  icon={item.hasOptionals ? SvgCheckCircle : SvgCancel}
                  color={item.hasOptionals ? "green" : "red"}
                  obscured={!item.hasOptionals}
                />
              </td>
              <td>{!!item.purchaseDate && formatDateNumeric(item.purchaseDate, i18n.language)}</td>
              <td>
                {!!item.plannedArrivalDate ? formatDateNumeric(item.plannedArrivalDate, i18n.language) : undefined}
              </td>
              <Table.Td noWrap align="right">
                {!!item.costNet &&
                  (!!item.costNet?.ForeignValue
                    ? formatCurrency(item.costNet.ForeignValue.Value, i18n.language, item.costNet.ForeignValue.Currency)
                    : formatCurrency(item.costNet.EuroValue, i18n.language))}
              </Table.Td>
              <Table.Td noWrap align="right">
                {!!item.costGross &&
                  (!!item.costGross?.ForeignValue
                    ? formatCurrency(
                        item.costGross.ForeignValue.Value,
                        i18n.language,
                        item.costGross.ForeignValue.Currency,
                      )
                    : formatCurrency(item.costGross.EuroValue, i18n.language))}
              </Table.Td>
              <td>
                <State
                  text={states.find((x) => x.value == item.state)?.label!}
                  color={item.state == "assigned" ? "green" : item.state == "arrived" ? "orange" : "red"}
                />
              </td>
              <td>{purchaseTypes.find((x) => x.value == item.purchaseType)?.label}</td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </Panel>

      {!!itemToDelete && (
        <DeleteCarPurchaseDialog carPurchaseId={itemToDelete.id} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToShowVersions && (
        <CarPurchaseHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}

      {!!showManufacturerDialog && (
        <EditManufacturerDialog
          manufacturerId={showManufacturerDialog}
          onClose={() => setShowManufacturerDialog(undefined)}
        />
      )}
    </>
  );
};

export default CarPurchases;
